import React from 'react';
import { DEFAULT_FIXED_TIMETABLE_NAME, comparePeriodKeys, draftHours, type FixedProgramDraft } from '../../utils/fixedProgramDrafts';

/** 원형 메인그린 체크(흰 체크) — 기본 파란 체크박스 대체 */
export const RoundCheck: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
}> = ({ checked, onChange, label, ariaLabel, disabled }) => (
  <span
    role="checkbox"
    tabIndex={disabled ? -1 : 0}
    aria-checked={checked}
    aria-label={ariaLabel}
    aria-disabled={disabled || undefined}
    className={`cp-aip-rc${checked ? ' is-on' : ''}${label ? ' has-label' : ''}${disabled ? ' is-disabled' : ''}`}
    onClick={() => !disabled && onChange(!checked)}
    onKeyDown={(e) => {
      if (disabled) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onChange(!checked);
      }
    }}
  >
    <span className="cp-aip-rc-dot" aria-hidden>
      <svg viewBox="0 0 16 16" width="11" height="11">
        <path d="M3.5 8.4l2.9 2.8 6-6.4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    {label ? <span className="cp-aip-rc-label">{label}</span> : null}
  </span>
);

export type PeriodOpt = { key: string; label: string; start?: string; end?: string };

/**
 * 교시 선택 블록: N시간이면 정확히 N개 블록.
 * 시간 수가 정해지지 않았으면 선택 교시 수 + 빈 블록 1개(추가용).
 * withNames 면 선택된 교시마다 아래에 그 교시의 시간표명 입력(기존 행사 등록 모달과 같은 방식).
 */
export const PeriodBlocks: React.FC<{
  draft: Pick<FixedProgramDraft, 'periods' | 'hours' | 'timetableName' | 'timetableNames'>;
  options: PeriodOpt[];
  onChange: (periods: string[]) => void;
  withNames?: boolean;
  onNamesChange?: (names: Record<string, string>) => void;
  namePlaceholder?: string;
}> = ({ draft, options, onChange, withNames, onNamesChange, namePlaceholder }) => {
  const fixedN = draft.hours && draft.hours > 0 ? draft.hours : 0;
  const n = fixedN || draft.periods.length + 1;
  const slots = Array.from({ length: Math.min(10, n) }, (_, i) => draft.periods[i] ?? '');
  const names = draft.timetableNames ?? {};
  const setAt = (i: number, key: string) => {
    const next = [...slots];
    const prevKey = next[i];
    next[i] = key;
    onChange(Array.from(new Set(next.filter(Boolean))).sort(comparePeriodKeys));
    /** 교시를 바꾸면 그 칸의 시간표명을 새 교시로 옮김 */
    if (withNames && onNamesChange && prevKey && names[prevKey] !== undefined) {
      const nm = { ...names };
      if (key) nm[key] = nm[prevKey];
      delete nm[prevKey];
      onNamesChange(nm);
    }
  };
  return (
    <div className={`cp-pb${withNames ? ' cp-pb--named' : ''}`} role="group" aria-label="교시 선택">
      {slots.map((v, i) => (
        <div key={i} className="cp-pb-col">
          <select
            className={`cp-pb-slot${v ? ' is-on' : ''}`}
            value={v}
            aria-label={`${i + 1}번째 교시`}
            onChange={(e) => setAt(i, e.target.value)}
          >
            <option value="">{fixedN || i < draft.periods.length ? '선택' : '＋'}</option>
            {options
              .filter((o) => o.key === v || !slots.includes(o.key))
              .map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
          </select>
          {withNames ? (
            v ? (
              <input
                className="cp-pb-name"
                value={names[v] ?? ''}
                placeholder={draft.timetableName.trim() || namePlaceholder || DEFAULT_FIXED_TIMETABLE_NAME}
                aria-label={`${v} 시간표명`}
                maxLength={10}
                onChange={(e) => onNamesChange?.({ ...names, [v]: e.target.value })}
              />
            ) : (
              <span className="cp-pb-name cp-pb-name--empty" aria-hidden>
                —
              </span>
            )
          ) : null}
        </div>
      ))}
    </div>
  );
};

/** 고정 프로그램 색 — 기존 행사 등록 모달과 같은 원형 팔레트 */
export const ColorCircles: React.FC<{ colors: string[]; value: string; onChange: (hex: string) => void }> = ({ colors, value, onChange }) => {
  const cur = (value || colors[0] || '').trim().toLowerCase();
  return (
    <div className="cp-colors" role="radiogroup" aria-label="색상">
      {colors.map((hex) => {
        const on = cur === hex.trim().toLowerCase();
        return (
          <button
            key={hex}
            type="button"
            role="radio"
            aria-checked={on}
            title={hex}
            aria-label={`고정 프로그램 색 ${hex}`}
            className={'cp-annual-event-fixed-color-btn cp-colors-btn' + (on ? ' cp-annual-event-fixed-color-btn-selected' : '')}
            onClick={() => onChange(hex)}
          >
            <span className="cp-annual-event-fixed-color-btn-inner" style={{ backgroundColor: hex }} />
          </button>
        );
      })}
    </div>
  );
};

/** 편성시간 'N시간' 스테퍼 — 줄이면 뒤 교시부터 빠짐 */
export const HoursStepper: React.FC<{
  draft: Pick<FixedProgramDraft, 'periods' | 'hours'>;
  onChange: (patch: { hours?: number; periods?: string[] }) => void;
  range?: string;
}> = ({ draft, onChange, range }) => {
  const h = draftHours(draft);
  const set = (v: number) => {
    const nv = Math.max(0, Math.min(10, v));
    onChange({ hours: nv || undefined, periods: draft.periods.slice(0, nv || draft.periods.length) });
  };
  return (
    <div className="cp-hs">
      <div className="cp-hs-row">
        <button type="button" aria-label="1시간 줄이기" onClick={() => set(h - 1)} disabled={h <= 0}>
          <svg className="cp-hs-glyph" viewBox="0 0 12 12" aria-hidden><path d="M2 6h8" /></svg>
        </button>
        <span className="cp-hs-val">{h}시간</span>
        <button type="button" aria-label="1시간 늘리기" onClick={() => set(h + 1)} disabled={h >= 10}>
          <svg className="cp-hs-glyph" viewBox="0 0 12 12" aria-hidden><path d="M2 6h8M6 2v8" /></svg>
        </button>
      </div>
      {range ? <span className="cp-hs-range">{range}</span> : null}
    </div>
  );
};

/** 적용클래스 원형 체크 목록 */
export const ClassChecks: React.FC<{
  options: Array<{ id: string; label: string }>;
  value: string[];
  onChange: (ids: string[]) => void;
}> = ({ options, value, onChange }) => (
  <div className="cp-cc" role="group" aria-label="적용클래스">
    {options.map((c) => {
      const on = value.includes(c.id);
      return (
        <RoundCheck
          key={c.id}
          checked={on}
          label={c.label}
          onChange={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])}
        />
      );
    })}
  </div>
);

/** 'YYYY-MM-DD' → 'YYYY년 MM월 DD일'(기존 고정 프로그램 관리 카드와 같은 형식) */
export function formatKorDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[1]}년 ${m[2]}월 ${m[3]}일` : '';
}

/** 테두리 없는 큰 제목형 날짜 — 누르면 달력 */
export const TitleDate: React.FC<{ value: string; onChange: (v: string) => void; missing?: boolean }> = ({ value, onChange, missing }) => {
  const ref = React.useRef<HTMLInputElement | null>(null);
  const open = () => {
    const el = ref.current;
    if (!el) return;
    try {
      (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    } catch {
      el.focus();
    }
  };
  return (
    <span className={`cp-tt-date${missing ? ' is-miss' : ''}`} onClick={open} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), open())} aria-label="날짜 선택">
      <span className="cp-tt-date-text">{formatKorDate(value) || '날짜 선택'}</span>
      <input ref={ref} type="date" className="cp-tt-date-input" value={value} onChange={(e) => onChange(e.target.value)} tabIndex={-1} aria-hidden />
    </span>
  );
};

/** 테두리 없는 큰 제목형 행사명 입력 */
export const TitleName: React.FC<{ value: string; onChange: (v: string) => void; missing?: boolean }> = ({ value, onChange, missing }) => {
  /** 긴 행사명은 잘리지 않고 여러 줄로 늘어난다 */
  const growRef = React.useRef<HTMLTextAreaElement | null>(null);
  React.useLayoutEffect(() => {
    const el = growRef.current;
    if (!el) return;
    let lastWidth = -1;
    const fit = () => {
      el.style.height = 'auto';
      const next = el.scrollHeight;
      el.style.height = next > 0 ? `${next}px` : '';
    };
    fit();
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width === lastWidth) return;
      lastWidth = width;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);
  return (
  <textarea
      ref={growRef}
      rows={1}
      className={`cp-tt-name cp-tt-name-grow${missing ? ' is-miss' : ''}`}
      value={value}
      placeholder="행사명 입력"
      maxLength={60}
      onChange={(e) => onChange(e.target.value.replace(/\n/g, ' '))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault();
      }}
      aria-label="행사명"
    />
  );
};

/**
 * 교시 블록 전체(수업 교시 + 방과후, 최대 10개) — 눌러서 선택/해제.
 * 선택된 교시 아래에 그 교시의 시간표명 입력.
 */
export const PeriodToggleGrid: React.FC<{
  draft: Pick<FixedProgramDraft, 'periods' | 'hours' | 'timetableName' | 'timetableNames'>;
  options: PeriodOpt[];
  onChange: (patch: { periods: string[]; hours?: number; timetableNames?: Record<string, string> }) => void;
  namePlaceholder?: string;
}> = ({ draft, options, onChange, namePlaceholder }) => {
  const names = draft.timetableNames ?? {};
  const toggle = (key: string) => {
    const on = draft.periods.includes(key);
    const periods = (on ? draft.periods.filter((x) => x !== key) : [...draft.periods, key]).sort(comparePeriodKeys);
    const hours = draft.hours && draft.hours > 0 && periods.length > draft.hours ? periods.length : draft.hours;
    onChange({ periods, hours });
  };
  return (
    <div className="cp-ptg" role="group" aria-label="교시 선택">
      {options.slice(0, 10).map((o) => {
        const on = draft.periods.includes(o.key);
        return (
          <div key={o.key} className={`cp-ptg-col${on ? ' is-on' : ''}`}>
            <button type="button" className="cp-ptg-block" aria-pressed={on} title={o.start ? `${o.start}~${o.end}` : undefined} onClick={() => toggle(o.key)}>
              {o.label}
            </button>
            {on ? (
              <input
                className="cp-pb-name"
                value={names[o.key] ?? ''}
                placeholder={draft.timetableName.trim() || namePlaceholder || DEFAULT_FIXED_TIMETABLE_NAME}
                aria-label={`${o.label} 시간표명`}
                maxLength={10}
                onChange={(e) => onChange({ periods: draft.periods, hours: draft.hours, timetableNames: { ...names, [o.key]: e.target.value } })}
              />
            ) : (
              <span className="cp-ptg-spacer" aria-hidden />
            )}
          </div>
        );
      })}
    </div>
  );
};

/** 적용클래스: 적용된 클래스만 칩으로 — 누르면 작은 선택 창(원형 그린 체크) */
export const ClassPickerChips: React.FC<{
  options: Array<{ id: string; label: string; grades?: string[] }>;
  value: string[];
  onChange: (ids: string[]) => void;
  missing?: boolean;
  defaultOpen?: boolean;
}> = ({ options, value, onChange, missing, defaultOpen }) => {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const wrap = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const chosen = options.filter((o) => value.includes(o.id));
  const allOn = options.length > 0 && chosen.length === options.length;
  return (
    <div className="cp-cpk" ref={wrap}>
      <button type="button" className={`cp-cpk-trigger${missing ? ' is-miss' : ''}`} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {chosen.length ? (
          chosen.map((c) => (
            /* 기존 '고정 프로그램 관리' 카드와 같은 클래스 블록(App.css 공용 클래스) */
            <span key={c.id} className="cp-fixed-manage-class-block cp-fixed-manage-class-block-compact cp-cpk-class">
              <span className="cp-grade-track-class-block-label">{c.label}</span>
              {(c.grades ?? []).map((n) => (
                <span key={n} className="cp-cpk-gnum">
                  {n}
                </span>
              ))}
            </span>
          ))
        ) : (
          <span className="cp-cpk-empty">클래스 선택</span>
        )}
      </button>
      {open ? (
        <div className="cp-cpk-pop" role="dialog" aria-label="적용클래스 선택">
          <div className="cp-cpk-pop-head">
            <strong>적용클래스</strong>
            <RoundCheck checked={allOn} label="전체" onChange={() => onChange(allOn ? [] : options.map((o) => o.id))} />
          </div>
          <div className="cp-cpk-pop-list">
            {options.map((o) => {
              const on = value.includes(o.id);
              return <RoundCheck key={o.id} checked={on} label={o.label} onChange={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])} />;
            })}
          </div>
          <button type="button" className="cp-cpk-done" onClick={() => setOpen(false)}>
            완료
          </button>
        </div>
      ) : null}
    </div>
  );
};
